import { Matrix4, Quaternion, Vector3 } from 'three';
import type { Pcg32 } from '../../../sim/rng';
import { type Batch, IDENTITY, put, TAU, yawQuat } from './batch';
import { MAX_SCORCH } from './batches';
import { BURSTS } from './bursts';
import type { EffectsToggles, EventOf, Fx } from './context';
import type { Drawer } from './particles';

/*
 * Explosions (05 §9): a stepped emissive fireball, rising smoke, debris on ballistic arcs,
 * a light, a scorch mark that stays, and whole-texel shake. Also the wreck of a destroyed prop.
 */

/** Fireball frames: [until (fraction of life), scale (x blast radius), step down the fire ramp]. */
const FIREBALL_FRAMES: ReadonlyArray<readonly [until: number, scale: number, step: number]> = [
  [0.1, 0.2, 0],
  [0.2, 0.47, 0],
  [0.3, 0.73, 1],
  [0.4, 1.0, 1],
  [0.6, 0.94, 2],
  [0.8, 0.78, 3],
  [1.0, 0.55, 3],
];

const _pos = new Vector3();
const _scale = new Vector3();
const _up = new Vector3();
const _quat = new Quaternion();
const _mat = new Matrix4();
const Y_AXIS = new Vector3(0, 1, 0);

/** With flashes turned down the ball starts at orange (step 2 of the ramp) and is a little smaller. */
const SOFT_FIRST_STEP = 2;
const SOFT_SCALE = 0.8;

/** Stepped, not blended: the ball jumps to the next pre-built mesh down the fire ramp. */
export function fireballDrawer(fire: readonly Batch[], toggles: EffectsToggles): Drawer {
  return (p, camera) => {
    const t = p.age / p.life;
    let scale = 0.55;
    let step = 3;
    for (let i = 0; i < FIREBALL_FRAMES.length; i++) {
      const f = FIREBALL_FRAMES[i];
      if (f && t < f[0]) {
        scale = f[1];
        step = f[2];
        break;
      }
    }
    if (!toggles.flashes) {
      step = Math.max(step, SOFT_FIRST_STEP);
      scale *= SOFT_SCALE;
    }
    p.batch = fire[step] ?? p.batch;
    const s = p.size * scale;
    put(p.batch, camera, p.x, p.y, p.z, s, s, s, IDENTITY);
  };
}

/** Persistent flat blots laid along the slope; past the cap the oldest is overwritten. */
export class ScorchDecals {
  private count = 0;

  constructor(
    private readonly batch: Batch,
    private readonly rng: Pcg32,
    private readonly groundY: (x: number, z: number) => number,
  ) {}

  add(x: number, z: number, radius: number): void {
    const mesh = this.batch.mesh;
    const e = Math.max(0.5, radius * 0.5);
    const slopeX = (this.groundY(x + e, z) - this.groundY(x - e, z)) / (2 * e);
    const slopeZ = (this.groundY(x, z + e) - this.groundY(x, z - e)) / (2 * e);
    _up.set(-slopeX, 1, -slopeZ).normalize();
    _quat.setFromUnitVectors(Y_AXIS, _up).multiply(yawQuat(this.rng.range(0, TAU)));
    _pos.set(x, this.groundY(x, z) + 0.05, z);
    _scale.set(radius, 1, radius);
    mesh.setMatrixAt(this.count % MAX_SCORCH, _mat.compose(_pos, _quat, _scale));
    this.count++;
    mesh.count = Math.min(this.count, MAX_SCORCH);
    mesh.visible = true;
    mesh.instanceMatrix.needsUpdate = true;
  }
}

export function explode(fx: Fx, e: EventOf<'explosion'>): void {
  const { rng, particles, set } = fx;
  const r = e.radius;
  const ground = fx.groundY(e.x, e.z);
  // Half buried, so the ball reads as a dome sitting on the ground.
  const first = set.fire[0] ?? set.b.cubeEmber;
  particles.spawn('fireball', first, e.x, ground + r * 0.3, e.z, r, e.big ? 0.42 : 0.3);

  const smokeCount = e.big ? 10 : 6 + rng.int(3);
  for (let i = 0; i < smokeCount; i++) {
    const a = rng.range(0, TAU);
    const d = rng.range(0, r * 0.45);
    const p = particles.puff(rng.pick(set.smoke), e.x + Math.sin(a) * d, ground + rng.range(0.4, 1.4), e.z + Math.cos(a) * d, r * rng.range(0.16, 0.28), rng.range(1.5, 2.2), rng.range(2.8, 3.6), rng.range(0.08, 0.3));
    p.vx = Math.sin(a) * 0.5;
    p.vz = Math.cos(a) * 0.5;
  }
  particles.burst(e.big ? BURSTS.bigBlast : BURSTS.blast, set.blastDebris, e.big ? 16 : 8 + rng.int(5), e.x, ground + 0.3, e.z, ground, r);

  fx.lights.flash(e.x, ground + 1.5, e.z, fx.blastColor, e.big ? 130 : 80, r * 3 + 8, 0.4, true);
  fx.scorch.add(e.x, e.z, r * (e.big ? 0.6 : 0.5));
  fx.shake.add(e.x, e.z, e.big ? 1 : 0.6);
}

/** A prop going to pieces. The event carries only the id, so the caller's lookup says where. */
export function wreck(fx: Fx, e: EventOf<'propDestroyed'>): void {
  const at = fx.propPosition?.(e.id);
  if (!at) return;
  const { rng, particles, set } = fx;
  const ground = fx.groundY(at.x, at.z);
  particles.burst(BURSTS.wreck, set.wreckDebris, 18, at.x, ground + 0.8, at.z, ground, 1);
  for (let i = 0; i < 4; i++) {
    const p = particles.puff(i < 2 ? set.b.dust : set.b.smokeMid, at.x + rng.range(-1, 1), ground + rng.range(0.4, 1.2), at.z + rng.range(-1, 1), rng.range(0.6, 0.95), rng.range(0.9, 1.5), 2.0, i * 0.05);
    p.vx = rng.range(-0.6, 0.6);
    p.vz = rng.range(-0.6, 0.6);
  }
}
